import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HiArrowLeft } from 'react-icons/hi2';

const UPDATED = 'July 24, 2026';
const API = import.meta.env.VITE_API_URL ?? '';

/* ─── Shared layout ──────────────────────────────────────── */
function LegalShell({ title, children }) {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100dvh', background: '#090817', maxWidth: 430, margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', position: 'sticky', top: 0, background: '#090817', zIndex: 10 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'rgba(255,255,255,0.07)', border: 'none', borderRadius: 12, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', flexShrink: 0 }}>
          <HiArrowLeft size={18} />
        </button>
        <h1 style={{ fontSize: '17px', fontWeight: 800, color: '#fff', margin: 0 }}>{title}</h1>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px 48px' }}>
        {children}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: '14px', fontWeight: 800, color: '#7C5CF6', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 10px' }}>{title}</h2>
      <div style={{ fontSize: '13px', color: '#C4C9E2', lineHeight: 1.8 }}>{children}</div>
    </div>
  );
}

function P({ children }) {
  return <p style={{ margin: '0 0 10px' }}>{children}</p>;
}

function Li({ children }) {
  return <li style={{ marginBottom: 6, paddingLeft: 4 }}>{children}</li>;
}

function Ul({ children }) {
  return <ul style={{ paddingLeft: 18, margin: '0 0 10px' }}>{children}</ul>;
}

/* ═══════════════════════════════════════════════════════════ */
/*  TERMS OF SERVICE                                           */
/* ═══════════════════════════════════════════════════════════ */
export function TermsOfService() {
  const [policies, setPolicies]     = useState('');
  const [policiesDate, setPoliciesDate] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/wallet/public-config`)
      .then(r => r.json())
      .then(d => {
        if (d.platformPolicies) setPolicies(d.platformPolicies);
        if (d.platformPoliciesUpdatedAt) setPoliciesDate(d.platformPoliciesUpdatedAt);
      })
      .catch(() => {});
  }, []);

  return (
    <LegalShell title="Terms of Service">
      <p style={{ fontSize: '12px', color: '#4B5170', marginBottom: 28 }}>Last updated: {UPDATED}</p>

      <Section title="1. Agreement to Terms">
        <P>By creating a wallet, accessing, or using the Ikhua platform ("Platform", "we", "us"), you confirm that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree, do not use the Platform.</P>
        <P>You must be at least 18 years old and legally able to enter into contracts in your jurisdiction to use Ikhua.</P>
      </Section>

      <Section title="2. What Ikhua Is">
        <P>Ikhua is a <strong style={{ color: '#fff' }}>custodial</strong> cryptocurrency wallet and peer-to-peer (P2P) trading platform. This means:</P>
        <Ul>
          <Li>Your funds are held by Ikhua in a platform-controlled wallet on your behalf.</Li>
          <Li>Ikhua manages the private keys associated with your account. You do not hold or need to manage a seed phrase.</Li>
          <Li>Access to your funds requires your Ikhua account credentials (email and password). Protect these carefully.</Li>
          <Li>Withdrawals to an external wallet are subject to identity verification (KYC) and platform fees.</Li>
        </Ul>
        <P>Ikhua is not a bank, financial institution, money services business, or investment adviser. We do not provide financial, legal, or tax advice. Funds held on the Platform are not covered by any deposit protection scheme.</P>
      </Section>

      <Section title="3. Eligibility & Prohibited Persons">
        <P>You may not use Ikhua if you are:</P>
        <Ul>
          <Li>Located in, a citizen of, or resident of a country subject to international sanctions (including but not limited to Iran, North Korea, Cuba, Syria, or Crimea).</Li>
          <Li>Listed on any sanctions list maintained by the UN, EU, US OFAC, UK OFSI, or any equivalent authority.</Li>
          <Li>Under 18 years of age.</Li>
          <Li>Previously banned from the Platform.</Li>
        </Ul>
        <P>By using Ikhua you represent and warrant that none of the above apply to you.</P>
      </Section>

      <Section title="4. Prohibited Activities">
        <P><strong style={{ color: '#EF4444' }}>The following are strictly prohibited and will result in immediate account termination, forfeiture of funds held in escrow, reporting to relevant authorities, and potential legal action:</strong></P>
        <Ul>
          <Li><strong style={{ color: '#fff' }}>Fraud:</strong> Any deceptive act including fake payment confirmations, chargeback fraud, impersonation, or misrepresentation of any kind.</Li>
          <Li><strong style={{ color: '#fff' }}>Money laundering:</strong> Using Ikhua to conceal, layer, or integrate proceeds of crime.</Li>
          <Li><strong style={{ color: '#fff' }}>Terrorism financing:</strong> Sending, receiving, or facilitating funds for terrorist organisations or individuals.</Li>
          <Li><strong style={{ color: '#fff' }}>Scamming:</strong> Creating fake offers, conducting exit scams, or refusing to release escrow after confirmed payment.</Li>
          <Li><strong style={{ color: '#fff' }}>Manipulation:</strong> Creating multiple accounts to manipulate ratings, inflate trade counts, or circumvent KYC requirements.</Li>
          <Li><strong style={{ color: '#fff' }}>Market manipulation:</strong> Wash trading, spoofing, or coordinating artificial price movements.</Li>
          <Li>Using the Platform to purchase or sell illegal goods or services.</Li>
          <Li>Attempting to hack, reverse-engineer, or otherwise compromise Platform security.</Li>
          <Li>Violating any applicable law or regulation in your jurisdiction.</Li>
        </Ul>
        <P>We actively monitor for suspicious activity and cooperate fully with law enforcement agencies upon receipt of lawful requests.</P>
      </Section>

      <Section title="5. KYC & Identity Verification">
        <P>Access to P2P trading requires successful identity verification (KYC). You agree to:</P>
        <Ul>
          <Li>Provide accurate, current, and complete identity information.</Li>
          <Li>Submit only genuine, unaltered government-issued identification documents.</Li>
          <Li>Submit to additional verification if requested.</Li>
        </Ul>
        <P>Submitting fraudulent, forged, or another person's identification documents is a criminal offence and will be reported to law enforcement.</P>
        <P>We reserve the right to reject any KYC application and to suspend or close accounts at our sole discretion.</P>
      </Section>

      <Section title="6. P2P Trading & Escrow">
        <P>When you trade on the P2P market:</P>
        <Ul>
          <Li>Cryptocurrency is held in a platform-controlled escrow wallet for the duration of the trade.</Li>
          <Li>Escrow is released only upon the seller's explicit confirmation of payment receipt, or by admin resolution of a dispute.</Li>
          <Li>A <strong style={{ color: '#fff' }}>2% platform fee</strong> (1% per party, capped at $20) is deducted from the escrow amount at release.</Li>
          <Li>Trades expire if escrow is not funded within 30 minutes of trade creation.</Li>
        </Ul>
        <P><strong style={{ color: '#EF4444' }}>Never release escrow until you have verified payment in your bank or mobile money account. Screenshots can be faked. Chargebacks are possible with certain payment methods — you bear this risk.</strong></P>
        <P>In disputes, our admin team reviews the chat history and evidence provided by both parties. Admin decisions on dispute resolution are final.</P>
        <P>Ikhua is not liable for losses arising from P2P trades, including fraud by counterparties, chargeback fraud, or failure to verify payments before releasing escrow.</P>
        <P><strong style={{ color: '#fff' }}>Trade partner contact:</strong> To facilitate dispute resolution before admin involvement, you may optionally add a WhatsApp or phone number to your profile. By doing so, you consent to that number being visible to your trade partner for the duration of the trade. You are solely responsible for any communications that take place outside of the Ikhua platform. Ikhua is not a party to and bears no responsibility for off-platform communications.</P>
      </Section>

      <Section title="7. Platform Fees">
        <Ul>
          <Li><strong style={{ color: '#fff' }}>Withdrawals:</strong> 3% of the withdrawal amount (minimum $3, maximum $25). Minimum withdrawal is $100 USD equivalent.</Li>
          <Li><strong style={{ color: '#fff' }}>P2P escrow release:</strong> 2% total (1% per party, capped at $20 per trade).</Li>
          <Li><strong style={{ color: '#fff' }}>Internal swap:</strong> 0.5% of the amount being swapped, deducted from the source coin.</Li>
          <Li>Minimum deposit amounts apply per coin. Deposits below the minimum accumulate on-chain and are credited to your balance once the threshold is reached.</Li>
          <Li>Network/miner fees for on-chain sweeps and withdrawals are borne by the Platform and recovered through the above fee structure.</Li>
          <Li>All fees are non-refundable once a transaction is processed.</Li>
          <Li>Fee rates are subject to change with notice. Current rates are always displayed on the Receive and Send screens.</Li>
        </Ul>
      </Section>

      <Section title="8. Transactions Are Irreversible">
        <P>Blockchain transactions are <strong style={{ color: '#EF4444' }}>permanent and irreversible</strong>. Ikhua cannot reverse, cancel, or refund any transaction once it has been broadcast to the blockchain. You are solely responsible for verifying recipient addresses before sending.</P>
      </Section>

      <Section title="9. Account Security">
        <P>You are solely responsible for:</P>
        <Ul>
          <Li>Keeping your account password and email access secure. Use a strong, unique password.</Li>
          <Li>The security of your device, browser, and email account.</Li>
          <Li>All activity that occurs under your account. If you suspect unauthorised access, contact support immediately.</Li>
          <Li>Enabling two-factor authentication on your email account for additional protection.</Li>
        </Ul>
        <P>Ikhua staff will never ask for your password, OTP code, or any sensitive account credential. Any person claiming to be Ikhua support and requesting these details is a scammer — do not comply and report it to us immediately at <span style={{ color: '#7C5CF6' }}>support@ikhua.net</span>.</P>
      </Section>

      <Section title="10. Limitation of Liability">
        <P>To the maximum extent permitted by law, Ikhua and its operators, employees, and agents shall not be liable for:</P>
        <Ul>
          <Li>Loss of funds due to your failure to secure your account credentials or email access.</Li>
          <Li>Losses from P2P trading, including fraud by counterparties.</Li>
          <Li>Losses due to blockchain network failures, delays, or congestion.</Li>
          <Li>Any indirect, incidental, consequential, or punitive damages.</Li>
          <Li>Service interruptions, downtime, or data loss.</Li>
        </Ul>
        <P>Your use of Ikhua is at your own risk. The Platform is provided "as is" without warranties of any kind.</P>
      </Section>

      <Section title="11. Termination">
        <P>We may suspend or terminate your access to Ikhua at any time, with or without notice, if we reasonably believe you have violated these Terms, engaged in fraudulent activity, or pose a risk to the Platform or other users.</P>
        <P>Upon termination, any funds held in active escrow will be handled on a case-by-case basis in accordance with dispute resolution procedures.</P>
      </Section>

      <Section title="12. Changes to Terms">
        <P>We may update these Terms from time to time. Continued use of Ikhua after changes are posted constitutes acceptance of the updated Terms. We will notify registered users of material changes via email.</P>
      </Section>

      <Section title="13. Governing Law">
        <P>These Terms are governed by applicable international law. Disputes shall be resolved through good-faith negotiation, failing which through binding arbitration. Nothing in these Terms limits either party's right to seek emergency injunctive relief.</P>
      </Section>

      <Section title="Contact">
        <P>For legal enquiries: <span style={{ color: '#7C5CF6' }}>support@ikhua.net</span></P>
      </Section>

      {policies && (
        <Section title="Platform Policies &amp; Additional Terms">
          {policiesDate && (
            <p style={{ fontSize: '11px', color: '#4B5170', margin: '0 0 10px' }}>
              Last updated: {new Date(policiesDate).toLocaleDateString()}
            </p>
          )}
          <pre style={{ fontFamily: 'inherit', fontSize: '13px', color: '#C4C9E2', lineHeight: 1.8, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
            {policies}
          </pre>
        </Section>
      )}
    </LegalShell>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  PRIVACY POLICY                                             */
/* ═══════════════════════════════════════════════════════════ */
export function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy">
      <p style={{ fontSize: '12px', color: '#4B5170', marginBottom: 28 }}>Last updated: {UPDATED}</p>

      <Section title="1. Overview">
        <P>Ikhua is designed to minimise the data we collect. Your seed phrase and private keys never leave your device and are never accessible to us. This policy explains what we do collect and why.</P>
      </Section>

      <Section title="2. Data We Collect">
        <P><strong style={{ color: '#fff' }}>Account data (registered users only):</strong></P>
        <Ul>
          <Li>Email address</Li>
          <Li>Display name (optional)</Li>
          <Li>Encrypted wallet backup (AES-256-GCM encrypted with your password — we cannot decrypt it)</Li>
          <Li>Wallet addresses (BTC, ETH, TRX) — public information by nature</Li>
          <Li>WhatsApp / phone number (optional — see Section 3 for how this is used)</Li>
        </Ul>
        <P><strong style={{ color: '#fff' }}>KYC data (P2P users only):</strong></P>
        <Ul>
          <Li>Government-issued ID photos (front and back)</Li>
          <Li>Selfie with ID</Li>
          <Li>Liveness verification photo</Li>
        </Ul>
        <P><strong style={{ color: '#fff' }}>Trade data:</strong></P>
        <Ul>
          <Li>Trade amounts, counterparty wallet addresses, payment methods, and chat messages</Li>
          <Li>This data is necessary for escrow management and dispute resolution</Li>
        </Ul>
        <P><strong style={{ color: '#fff' }}>Technical data:</strong></P>
        <Ul>
          <Li>Server logs (IP address, request timestamps) — retained for 30 days for security purposes</Li>
        </Ul>
      </Section>

      <Section title="3. How We Use Your Data">
        <Ul>
          <Li>To operate the wallet, P2P trading, and escrow services</Li>
          <Li>To verify your identity for KYC compliance</Li>
          <Li>To send transactional emails (trade updates, verification, security alerts)</Li>
          <Li>To detect and prevent fraud, money laundering, and prohibited activities</Li>
          <Li>To comply with legal obligations and respond to lawful authority requests</Li>
        </Ul>
        <P>We do not sell your data to third parties. We do not use your data for advertising.</P>
        <P><strong style={{ color: '#fff' }}>Phone number sharing between trade partners:</strong></P>
        <P>If you voluntarily add a WhatsApp or phone number to your profile, that number will be disclosed to the counterparty of any active trade you are a party to, for the sole purpose of direct communication to resolve trade matters. It is not shared with anyone else, not used for marketing, and not disclosed outside of an active trade context. You may remove your phone number from Profile settings at any time — removal takes effect on future trades and does not affect any trade already in progress.</P>
        <P>By adding a phone number to your profile, you explicitly consent to it being shared with your trade partners as described above.</P>
      </Section>

      <Section title="4. Third-Party Services">
        <P>We use the following third-party services to operate the Platform:</P>
        <Ul>
          <Li><strong style={{ color: '#fff' }}>Cloudinary</strong> — stores KYC document photos securely</Li>
          <Li><strong style={{ color: '#fff' }}>Resend</strong> — sends transactional emails</Li>
          <Li><strong style={{ color: '#fff' }}>MongoDB Atlas</strong> — encrypted database hosting</Li>
          <Li><strong style={{ color: '#fff' }}>Railway</strong> — backend infrastructure hosting</Li>
          <Li><strong style={{ color: '#fff' }}>Cloudflare</strong> — CDN, DDoS protection, and DNS</Li>
          <Li><strong style={{ color: '#fff' }}>TronGrid / Infura / Blockstream</strong> — blockchain data APIs (public wallet addresses are queried)</Li>
          <Li><strong style={{ color: '#fff' }}>ChangeNOW</strong> — cryptocurrency swap service</Li>
        </Ul>
        <P>Each of these providers has their own privacy policy and data practices.</P>
      </Section>

      <Section title="5. KYC Document Retention">
        <P>KYC documents (ID photos) are retained for as long as your account is active and for a minimum of 5 years after account closure, as required by anti-money laundering regulations. You may request deletion subject to our compliance obligations.</P>
      </Section>

      <Section title="6. Data Security">
        <Ul>
          <Li>All data is transmitted over TLS/HTTPS.</Li>
          <Li>Your wallet backup is encrypted with AES-256-GCM using a key derived from your password — we cannot read it.</Li>
          <Li>KYC documents are stored in an access-controlled cloud environment.</Li>
          <Li>Passwords are hashed using bcrypt and never stored in plaintext.</Li>
        </Ul>
      </Section>

      <Section title="7. Your Rights">
        <P>Depending on your jurisdiction, you may have the right to:</P>
        <Ul>
          <Li>Access the personal data we hold about you</Li>
          <Li>Correct inaccurate data</Li>
          <Li>Request deletion of your account and associated data (subject to legal retention obligations)</Li>
          <Li>Object to or restrict certain processing</Li>
        </Ul>
        <P>To exercise these rights, contact us at <span style={{ color: '#7C5CF6' }}>support@ikhua.net</span>.</P>
      </Section>

      <Section title="8. Cookies">
        <P>Ikhua does not use tracking or advertising cookies. We use local browser storage (localStorage) solely to store your encrypted wallet state on your device.</P>
      </Section>

      <Section title="9. Children">
        <P>Ikhua is not directed at persons under 18. We do not knowingly collect data from minors. If we become aware that a minor has created an account, we will close it immediately.</P>
      </Section>

      <Section title="10. Changes">
        <P>We may update this Privacy Policy. Material changes will be notified to registered users via email. Continued use after changes constitutes acceptance.</P>
      </Section>

      <Section title="Contact">
        <P>Privacy enquiries: <span style={{ color: '#7C5CF6' }}>support@ikhua.net</span></P>
      </Section>
    </LegalShell>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  Combined route component (tab switcher)                    */
/* ═══════════════════════════════════════════════════════════ */
export default function Legal() {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'privacy' ? 'privacy' : 'terms';

  if (tab === 'privacy') return <PrivacyPolicy />;
  return <TermsOfService />;
}
